import { Typography } from 'app/components/ui';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { LabeledSection } from './LabeledSection';
import { DimensionsSection } from './DimensionsSection';
import { CalculationsSection } from './CalculationsSection';
import { NotesSection } from './NotesSection';
import { TagContentBlocks } from './TagContentBlocks';
import { Analysis } from './Analysis';
import { StatusField } from './StatusField';
import { ScaleField } from './ScaleField';
import { AccuracyField } from './AccuracyField';
import { CurrencyField } from './CurrencyField';
import { DateField } from './DateField';
import { FormatField } from './FormatField';
import { i18n } from 'app/utils/i18n';
import { TagWithMetadata, useCalculations, useDeleteTag, useDocumentTaxonomyConfig, useExtensions, usePresentationTree } from 'app/api/tag-queries';
import { CalculationGroupResponse, CalculationRuleSummandInput } from 'app/api/tag-queries/types';
import { useCreateCalculationRule, useUpdateCalculationRule } from 'app/api/tag-queries/calculation-rules';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { useSlate } from 'slate-react';
import { Editor as SlateEditor, Element, Node } from 'slate';
import { buildChainContaining } from '../Footer/tagChainUtils';
import { NoDefaultWarning } from './NoDefaultWarning';
import { getTagLabel, getConceptTypeContent } from 'app/utils/tag-utils';
import { TagContext } from 'app/state/contexts/TagContext';
import { SignSwitch } from './SignSwitch';
import { NilSwitch } from './NilSwitch';
import { EscapeSwitch } from './EscapeSwitch';
import { HiddenSwitch } from './HiddenSwitch';
import { HiddenValueField } from './HiddenValueField';
import { FractionValueFields } from './FractionValueFields';
import { FootnotesSection } from './FootnotesSection';
import { buildNegatedPresentationIndex, computeSignHint } from './signNegatedHint';
import { convertTagToExclude } from 'app/slate/extensions/tag/utils/exclusion';
import { TagInfoFooter } from '../Footer/TagInfoFooter';

import './TagInfo.scss';

interface TagInfoProps {
  selectedConceptName: string | null;
  selectedTag: TagWithMetadata | null;
  onDeleteClick?: (tag: TagWithMetadata) => void;
  onSelectTag?: (tag: TagWithMetadata | null) => void;
  isContentMarked: boolean;
  documentTags?: TagWithMetadata[];
  readOnly?: boolean;
  onCancel?: () => void;
}

export const TagInfo = ({
  selectedConceptName,
  selectedTag: activeTag,
  onDeleteClick,
  onSelectTag,
  isContentMarked,
  documentTags,
  readOnly = false,
  onCancel,
}: TagInfoProps) => {
  const { pDocument, references } = useEditorSelector((editor: any) => editor);
  const editor = useSlate();
  const { data: config } = useDocumentTaxonomyConfig(pDocument?.superId);
  const { tagLanguage, calcPickMode, setCalcPickMode, setTermHighlightTagIds } = useContext(TagContext);
  const updateRule = useUpdateCalculationRule();
  const createRule = useCreateCalculationRule();
  const pickContext = useRef<{ ruleId: string | null; summandsByConcept: Map<string, CalculationRuleSummandInput> } | null>(null);
  const { mutate: deleteTagMutation } = useDeleteTag();
  const { data: presentationTree } = usePresentationTree(pDocument?.superId);
  const { data: extensions } = useExtensions(pDocument?.superId, activeTag?.taxonomyId);
  const negatedIndex = useMemo(() => buildNegatedPresentationIndex(presentationTree ?? null), [presentationTree]);

  const taggedValue = useMemo(() => {
    if (!activeTag) return [];

    const conceptName = activeTag.concept?.name;
    const sameConcept = (documentTags ?? []).filter((t) => t.concept?.name === conceptName);
    if (sameConcept.length === 0) return [];

    const chain = buildChainContaining(sameConcept, activeTag.id);
    if (chain.length === 0) return [];

    const stripExt = (s: string) => s.replace(/\.[^./\\]+$/, '');

    const findInlineText = (tagId: string): string | null => {
      try {
        const entry = Array.from(
          SlateEditor.nodes(editor, {
            at: [],
            match: (n) => Element.isElement(n) && (n as any).type === 'tag' && (n as any).tagId === tagId,
          })
        )[0];
        if (!entry) return null;
        return Node.string(entry[0] as Node);
      } catch {
        return null;
      }
    };

    const findElementNode = (uuid: string | undefined): Node | null => {
      if (!uuid) return null;
      try {
        const entry = Array.from(
          SlateEditor.nodes(editor, {
            at: [],
            match: (n) => Element.isElement(n) && (n as any).uuid === uuid,
          })
        )[0];
        return entry ? (entry[0] as Node) : null;
      } catch {
        return null;
      }
    };

    const findTableCellValue = (tag: TagWithMetadata): string | null => {
      const ref = (references ?? []).find((r: any) => r.superId === (tag as any).onId);
      if (!ref?.selectionContent) return null;
      for (const row of ref.selectionContent) {
        for (const cell of row ?? []) {
          if (Array.isArray(cell?.tags) && cell.tags.includes(tag.id)) {
            return String(cell.value ?? '');
          }
        }
      }
      return null;
    };

    return chain.map((tag) => {
      const carrier = tag.metadata?.blockCarrier;
      let value = '';
      let list: { ordered: boolean; items: string[] } | undefined;
      if (carrier?.kind === 'image') {
        value = `${i18n('tag.content.chain.image')}: ${stripExt(carrier.displayName)}`;
      } else if (carrier?.kind === 'reference-table') {
        value = `${i18n('tag.content.chain.table')}: ${carrier.displayName}`;
      } else {
        const inline = findInlineText(tag.id);
        const cellValue = inline == null ? findTableCellValue(tag) : null;
        if (inline != null) {
          value = inline;
        } else if (cellValue) {
          value = cellValue;
        } else {
          const node = findElementNode((tag as any).onId);
          const nodeType = node && Element.isElement(node) ? (node as any).type : null;
          if (node && (nodeType === 'bulleted-list' || nodeType === 'numbered-list')) {
            const items = ((node as any).children ?? [])
              .filter((c: any) => c?.type === 'list-item')
              .map((li: Node) => Node.string(li).trim())
              .filter((t: string) => t.length > 0);
            list = { ordered: nodeType === 'numbered-list', items };
            value = items.join(' ');
          } else {
            value = (node ? Node.string(node).trim() : '') || tag.metadata?.textSnippet || '';
          }
        }
      }
      return {
        tagId: tag.id,
        value,
        list,
        location: {
          section: tag.metadata?.sectionName || '',
          page: tag.metadata?.pageNumber || 0,
        },
      };
    });
  }, [editor, activeTag, documentTags, references]);

  const conceptName = activeTag?.concept?.name || selectedConceptName;
  const {
    data: calculationGroups,
    isLoading: calculationsLoading,
    isFetching: calculationsFetching,
    isPreviousData: calculationsIsPreviousData,
  } = useCalculations(pDocument?.superId, conceptName, activeTag?.contextId, tagLanguage, activeTag?.id);

  useEffect(() => {
    if (!activeTag || !calculationGroups) {
      setTermHighlightTagIds(new Set());
      return;
    }
    const ids = new Set<string>();
    for (const group of calculationGroups) {
      const parentRow = group.rows.find((r) => r.isParent);
      if (!parentRow?.conceptId || parentRow.conceptId !== activeTag.conceptId) continue;
      for (const row of group.rows) {
        if (!row.isParent && row.tagId) ids.add(row.tagId);
      }
    }
    setTermHighlightTagIds(ids);
    return () => setTermHighlightTagIds(new Set());
  }, [activeTag, calculationGroups, conceptName, setTermHighlightTagIds]);

  const exitPickMode = useCallback(() => {
    pickContext.current = null;
    setCalcPickMode(null);
  }, [setCalcPickMode]);

  useEffect(() => {
    if (!calcPickMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitPickMode();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [calcPickMode, exitPickMode]);

  const enterPickMode = useCallback(
    (group: CalculationGroupResponse | null) => {
      if (!activeTag) return;
      const summandsByConcept = new Map<string, CalculationRuleSummandInput>();
      const initialTerms = new Set<string>();
      const initialPickedTagIds = new Set<string>();
      for (const row of group?.rows ?? []) {
        if (row.isParent || !row.conceptId) continue;
        initialTerms.add(row.conceptId);
        if (row.tagId) initialPickedTagIds.add(row.tagId);
        summandsByConcept.set(row.conceptId, {
          summandConceptId: row.conceptId,
          weight: row.weight,
          ...(row.dimQualifiers && row.dimQualifiers.length > 0
            ? { dimQualifiers: row.dimQualifiers.map((q) => ({ axisConceptId: q.axisConceptId, memberConceptId: q.memberConceptId })) }
            : {}),
        });
      }
      pickContext.current = { ruleId: group?.ruleId ?? null, summandsByConcept };
      setCalcPickMode({
        parentConceptName: conceptName,
        parentConceptId: activeTag.conceptId,
        parentTagId: activeTag.id,
        termConceptIds: initialTerms,
        pickedTagIds: initialPickedTagIds,
        onToggleTerm: (conceptId: string, tagId?: string) => {
          setCalcPickMode((prev: any) => {
            if (!prev) return prev;
            const next = new Set(prev.termConceptIds);
            const nextPicked = new Set(prev.pickedTagIds ?? []);
            if (next.has(conceptId)) {
              next.delete(conceptId);
              if (tagId) nextPicked.delete(tagId);
            } else {
              next.add(conceptId);
              if (tagId) nextPicked.add(tagId);
            }
            return { ...prev, termConceptIds: next, pickedTagIds: nextPicked };
          });
        },
      });
    },
    [activeTag, conceptName, setCalcPickMode]
  );

  const savePickMode = useCallback(() => {
    if (!calcPickMode || !pickContext.current || !activeTag || !pDocument?.superId) return;
    const summands: CalculationRuleSummandInput[] = [...calcPickMode.termConceptIds].map(
      (conceptId: string) => pickContext.current?.summandsByConcept.get(conceptId) ?? { summandConceptId: conceptId }
    );
    if (summands.length === 0) {
      exitPickMode();
      return;
    }
    if (pickContext.current.ruleId) {
      updateRule.mutate({ ruleId: pickContext.current.ruleId, documentSuperId: pDocument.superId, summands });
    } else {
      createRule.mutate({ documentSuperId: pDocument.superId, parentConceptId: activeTag.conceptId, linkroleUri: null, summands });
    }
    exitPickMode();
  }, [calcPickMode, activeTag, pDocument, updateRule, createRule, exitPickMode]);

  if (!activeTag && selectedConceptName) {
    return (
      <div className="p-3 w-100">
        <Typography>{i18n('tag.info.only.taxonomy')}</Typography>
      </div>
    );
  }

  if (!activeTag) {
    return (
      <div className="p-3 w-100">
        <Typography>{i18n('tag.no.valid.tag')}</Typography>
      </div>
    );
  }

  const label = activeTag.concept?.labels ? getTagLabel({ languages: activeTag.concept.labels, currentLang: tagLanguage }) : activeTag.concept?.name || '';
  const conceptType = activeTag.concept?.conceptType || null;
  const { conceptLabel } = conceptType ? getConceptTypeContent(conceptType) : { conceptLabel: i18n('tag.concept.default') };
  const documentation = activeTag.concept?.labels?.[tagLanguage]?.documentation || '';

  const preferredAccuracyType: 'decimals' | 'precision' = (config?.preferredFormat as 'decimals' | 'precision') ?? 'decimals';
  const tagCurrency = activeTag.unit?.measure?.replace(/^iso4217:/, '') ?? null;
  const configActiveCurrency = config?.currencySelections?.find((cur) => cur.isActive)?.currency ?? null;
  const documentSuperId = pDocument?.superId;

  const hasIncompleteConfig =
    !config ||
    !config.entity ||
    !config.contextSelections?.length ||
    !config.currencySelections?.length ||
    config.scale == null ||
    (config.decimals == null && config.precision == null);

  const hiddenSnapshot = taggedValue[0]?.value ?? activeTag.metadata?.textSnippet ?? '';
  const activeConceptName = activeTag.concept?.name ?? '';
  const isExtensionConcept = (extensions ?? []).some((e) => e.id === activeTag.conceptId || e.name === activeConceptName.replace(/^[^:]*:/, ''));
  const signHint = computeSignHint({
    sign: activeTag.sign ?? null,
    conceptKeys: [activeConceptName, activeConceptName.replace(/^[^:]*:/, '')],
    negatedIndex,
    isExtensionConcept,
  });

  return (
    <>
      <section className="tag-info p-3 w-100">
        <div className="flex-row">
          <span />
          <StatusField tagId={activeTag.id} documentSuperId={documentSuperId} currentStatus={activeTag.status ?? 'draft'} disabled={readOnly} />
        </div>

        <LabeledSection label={conceptLabel}>
          <div className="flex-column">
            <Typography variant="body-8">{label}</Typography>
            <Typography variant="body-3" fontWeight="light" color="#000000" style={{ wordBreak: 'break-all' }}>
              {activeTag.concept?.name || selectedConceptName || ''}
            </Typography>
          </div>
        </LabeledSection>

        <LabeledSection label={i18n('tag.documentation.title')}>
          <Typography variant="body-7" fontWeight="light" style={{ wordBreak: 'break-all' }}>
            {documentation || i18n('tag.documentation.empty')}
          </Typography>
        </LabeledSection>

        <NotesSection key={activeTag.id} defaultValue={activeTag.notes ?? ''} tagId={activeTag.id} documentSuperId={documentSuperId} disabled={readOnly} />

        <TagContentBlocks taggedValue={taggedValue} activeTagId={activeTag.id} />

        <DimensionsSection tag={activeTag} documentSuperId={documentSuperId} readOnly={readOnly} />

        <FootnotesSection tagId={activeTag.id} documentSuperId={documentSuperId} disabled={readOnly} />

        <LabeledSection label={i18n('tag.date.label')}>
          <DateField
            tagId={activeTag.id}
            documentSuperId={documentSuperId}
            selectedContextId={activeTag.contextId}
            tagContext={activeTag.context ?? null}
            contextSelections={config?.contextSelections ?? []}
            configId={config?.id}
            disabled={readOnly}
          />
        </LabeledSection>

        <LabeledSection label={i18n('tag.properties.title')}>
          {activeTag.type === 'nonFraction' && (
            <>
              {hasIncompleteConfig && <NoDefaultWarning />}

              <CurrencyField
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                selectedCurrency={tagCurrency ?? configActiveCurrency}
                currencySelections={config?.currencySelections ?? []}
                unit={activeTag.unit ?? null}
                configId={config?.id}
                disabled={readOnly}
              />

              <FormatField
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                currentFormat={activeTag.format ?? config?.format ?? null}
                disabled={readOnly}
              />

              <ScaleField tagId={activeTag.id} documentSuperId={documentSuperId} currentScale={activeTag.scale ?? config?.scale ?? null} disabled={readOnly} />

              <AccuracyField
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                tagDecimals={activeTag.decimals ?? null}
                tagPrecision={activeTag.precision ?? null}
                configDecimals={config?.decimals ?? null}
                configPrecision={config?.precision ?? null}
                preferredType={preferredAccuracyType}
                disabled={readOnly}
              />

              <SignSwitch tagId={activeTag.id} documentSuperId={documentSuperId} currentSignValue={activeTag.sign ?? null} disabled={readOnly} />

              {signHint && (
                <Typography variant="body-1" style={{ color: '#7a5c1e' }}>
                  {i18n('tag.sign.hint.no-negated-presentation')}
                </Typography>
              )}
            </>
          )}

          {activeTag.type === 'fraction' && (
            <CurrencyField
              tagId={activeTag.id}
              documentSuperId={documentSuperId}
              selectedCurrency={tagCurrency ?? configActiveCurrency}
              currencySelections={config?.currencySelections ?? []}
              unit={activeTag.unit ?? null}
              configId={config?.id}
              disabled={readOnly}
            />
          )}

          <div className="property-toggles">
            <NilSwitch tagId={activeTag.id} documentSuperId={documentSuperId} currentNilValue={activeTag.nil ?? false} disabled={readOnly} />

            {activeTag.type === 'nonNumeric' && (
              <EscapeSwitch
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                currentEscapeValue={activeTag.escape ?? null}
                conceptType={conceptType}
                disabled={readOnly || (activeTag.nil ?? false)}
              />
            )}

            <HiddenSwitch
              tagId={activeTag.id}
              documentSuperId={documentSuperId}
              currentHiddenValue={activeTag.hidden ?? false}
              snapshotValue={hiddenSnapshot}
              isPlaced={!!(activeTag as any).onId}
              disabled={readOnly}
            />
          </div>

          {activeTag.hidden &&
            (activeTag.type === 'fraction' ? (
              <FractionValueFields
                key={`fraction-value-${activeTag.id}`}
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                defaultValue={activeTag.value ?? ''}
                disabled={readOnly}
              />
            ) : (
              <HiddenValueField
                key={`hidden-value-${activeTag.id}`}
                tagId={activeTag.id}
                documentSuperId={documentSuperId}
                defaultValue={activeTag.value ?? ''}
                disabled={readOnly}
              />
            ))}
        </LabeledSection>
        {activeTag.type === 'nonFraction' && (
          <>
            {onSelectTag && <Analysis activeTag={activeTag} documentSuperId={documentSuperId} onSelectTag={onSelectTag} documentTags={documentTags ?? []} />}
            {calcPickMode && (
              <div
                className="flex flex-row justify-between"
                style={{ alignItems: 'center', gap: 8, padding: '4px 6px', backgroundColor: '#fdebc8', borderRadius: 4 }}
              >
                <Typography variant="body-1" style={{ color: '#7a5c1e', minWidth: 0 }}>
                  {i18n('tag.calculations.pick-mode-hint')}
                </Typography>
                <span className="flex flex-row" style={{ gap: 10, flexShrink: 0 }}>
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700, color: '#7a5c1e' }}
                    onClick={savePickMode}
                  >
                    {i18n('tag.calculations.pick-done')}
                  </button>
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#7a5c1e' }}
                    onClick={exitPickMode}
                  >
                    {i18n('tag.calculations.pick-cancel')}
                  </button>
                </span>
              </div>
            )}
            <CalculationsSection
              calculationGroups={calculationGroups}
              isLoading={calculationsLoading}
              isFetching={calculationsFetching}
              isPreviousData={calculationsIsPreviousData}
              authoring={
                !readOnly && documentSuperId && conceptName
                  ? {
                      documentSuperId,
                      parentConceptId: activeTag.conceptId,
                      selectedConceptName: conceptName,
                      documentTags: documentTags ?? [],
                      onPickTerms: enterPickMode,
                      onCreateSum: () => enterPickMode(null),
                    }
                  : undefined
              }
            />
          </>
        )}

        {!readOnly && !activeTag.hidden && !activeTag.metadata?.tableName && !activeTag.metadata?.blockCarrier && (
          <button
            className="btn btn-secondary w-100 mb-2"
            onClick={() => {
              if (convertTagToExclude(editor, activeTag.id)) {
                deleteTagMutation({ id: activeTag.id, documentSuperId, skipSectionRewrite: true });
                onCancel?.();
              }
            }}
          >
            {i18n('tag.exclude.convert')}
          </button>
        )}

        {onDeleteClick && (
          <button className="tag-info__delete-btn" onClick={() => onDeleteClick(activeTag)}>
            <i className="fa-regular fa-trash" />
            <Typography variant="body-7" fontWeight="semibold" color="#fff">
              {i18n('tag.delete.button')}
            </Typography>
          </button>
        )}
      </section>

      <TagInfoFooter selectedTag={activeTag} isContentMarked={isContentMarked} documentTags={documentTags ?? []} onCancel={onCancel} />
    </>
  );
};
